// @flow
import * as React from 'react';

import type { User } from '@/types/models';
import type { Router } from '@/types/router';

import { withRouter } from 'next/router';
import { withInstance, type WithInstanceProps } from '@/utils/HOC';
import { CookiesContext } from '@/globals/contexts';
import { consentEvent } from '@/utils/googleTagManager';
import { clearTrackingCookies, setConsentCookie } from '@/helpers/cookies';
import {
  CONSENT_SET_COOKIE_NAME,
  CONSENT_PERFORMANCE_COOKIE,
  CONSENT_TARGETING_COOKIE,
  CONSENT_SOCIAL_COOKIE,
} from '@/constants/cookies';
import { getCookieDomain } from '@/helpers/url';
import useSiteOptions from '@/hooks/useSiteOptions';

import { Flex, Text, Switch } from '@/components/new';

type OwnProps = {|
  user: ?User,
|};

type Props = {|
  ...OwnProps,
  ...WithInstanceProps,
  router: Router,
|};

function CookieSettingsPage({ instanceName, router }: Props): React.Node {
  const siteOptions = useSiteOptions();
  const { cookies, setCookie } = React.useContext(CookiesContext);
  const consentSetCookie = parseInt(cookies[CONSENT_SET_COOKIE_NAME], 10) || 0;
  const [performanceCookieValue, setPerformanceCookieValue] = React.useState(
    parseInt(cookies[CONSENT_PERFORMANCE_COOKIE.name], 10) ||
      CONSENT_PERFORMANCE_COOKIE.default
  );
  const [targetingCookieValue, setTargetingCookieValue] = React.useState(
    parseInt(cookies[CONSENT_TARGETING_COOKIE.name], 10) ||
      CONSENT_TARGETING_COOKIE.default
  );
  const [socialCookieValue, setSocialCookieValue] = React.useState(
    parseInt(cookies[CONSENT_SOCIAL_COOKIE.name], 10) ||
      CONSENT_SOCIAL_COOKIE.default
  );

  const domain = getCookieDomain();

  React.useEffect(() => {
    if (consentSetCookie !== 1) {
      setConsentCookie(
        setCookie,
        CONSENT_PERFORMANCE_COOKIE.name,
        CONSENT_PERFORMANCE_COOKIE.default,
        domain
      );
      setConsentCookie(
        setCookie,
        CONSENT_TARGETING_COOKIE.name,
        CONSENT_TARGETING_COOKIE.default,
        domain
      );
      setConsentCookie(
        setCookie,
        CONSENT_SOCIAL_COOKIE.name,
        CONSENT_SOCIAL_COOKIE.default,
        domain
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = React.useCallback(() => {
    setConsentCookie(setCookie, CONSENT_SET_COOKIE_NAME, 1, domain);
    setConsentCookie(
      setCookie,
      CONSENT_TARGETING_COOKIE.name,
      targetingCookieValue,
      domain
    );
    setConsentCookie(
      setCookie,
      CONSENT_SOCIAL_COOKIE.name,
      socialCookieValue,
      domain
    );
    setConsentCookie(
      setCookie,
      CONSENT_PERFORMANCE_COOKIE.name,
      performanceCookieValue,
      domain
    );

    consentEvent({
      performanceCookieValue,
      targetingCookieValue,
      socialCookieValue,
    });
    clearTrackingCookies({
      performanceCookieValue,
      targetingCookieValue,
      socialCookieValue,
    });
    router.push('/p/home');
  }, [
    performanceCookieValue,
    targetingCookieValue,
    socialCookieValue,
    router,
    domain,
    setCookie,
  ]);

  return (
    <div className="Page-body simple-page cookie-settings-page">
      <div className="content-container">
        <Text isTitle size={2} element="h1">
          Paramétrer les cookies
        </Text>

        <Text element="p">
          Lorsque vous naviguez sur {siteOptions.siteTitle}, des cookies seront
          déposés sur votre navigateur. Pour certains d’entre eux, votre
          consentement est nécessaire. Pour bénéficier de l’ensemble des
          fonctionnalités proposées par {siteOptions.siteTitle} (partage
          d’articles sur les réseaux sociaux, publicités conformes à vos centres
          d’intérêt, amélioration du site grâce aux statistiques de navigation,
          etc.), il est conseillé de garder l’activation des différentes
          catégories de cookies.
        </Text>

        <div style={{ marginTop: 50 }}>
          <div className="title-wrapper">
            <Text isTitle size={5} element="h2">
              Cookies non soumis à consentement
            </Text>
            <Switch label=" " disabled value onChange={() => {}} />
          </div>

          <p>
            Il s’agit de cookies qui garantissent le bon fonctionnement de{' '}
            {siteOptions.siteTitle} et permettent son optimisation. Le site Web
            ne peut pas fonctionner correctement sans ces cookies. Ils nous
            permettent de garantir le bon fonctionnement du site et de gérer les
            achats qui y sont faits.
          </p>
        </div>

        <div style={{ marginTop: 50 }}>
          <div className="title-wrapper">
            <Text isTitle size={5} element="h2">
              Cookies d'analyse
            </Text>
            <Switch
              label=" "
              value={performanceCookieValue === 1}
              onChange={(newValue) =>
                setPerformanceCookieValue(newValue ? 1 : 0)
              }
            />
          </div>

          <p>
            Ces cookies permettent d’obtenir des statistiques de fréquentation
            anonymes du site de {siteOptions.siteTitle} afin d’optimiser son
            ergonomie, sa navigation et ses contenus. En désactivant ces
            cookies, nous ne pourrons pas analyser le trafic de{' '}
            {siteOptions.siteTitle}
          </p>

          <ul>
            <li>Google Analytics</li>
          </ul>
        </div>

        <div style={{ marginTop: 50 }}>
          <div className="title-wrapper">
            <Text isTitle size={5} element="h2">
              Cookies sociaux
            </Text>
            <Switch
              label=" "
              value={socialCookieValue === 1}
              onChange={(newValue) => setSocialCookieValue(newValue ? 1 : 0)}
            />
          </div>

          <p>
            Ces cookies permettent d’interagir depuis le site{' '}
            {siteOptions.siteTitle} avec les modules sociaux et de partager les
            contenus du site, lorsque vous cliquez sur les modules « Partager »,
            « Aimer » de Facebook et de Twitter, par exemple. En désactivant ces
            cookies, vous ne pourrez plus partager les articles depuis{' '}
            {siteOptions.siteTitle} sur les réseaux sociaux.
          </p>
          <p>
            D'autre part, combinés aux données de navigation, les cookies
            sociaux permettent à {siteOptions.siteTitle} de créer et de diffuser
            certaines publicités personnalisées relatives à ses publications et
            à ses offres.
          </p>

          <p>Veuillez trouver la liste des cookies sociaux utilisés :</p>
          <ul>
            <li>Twitter</li>
            <li>Facebook</li>
            <li>Pixel Facebook</li>
          </ul>
        </div>

        <div style={{ marginTop: 50 }}>
          <div className="title-wrapper">
            <Text isTitle size={5} element="h2">
              Cookies de ciblage publicitaire
            </Text>
            <Switch
              label=" "
              value={targetingCookieValue === 1}
              onChange={(newValue) => setTargetingCookieValue(newValue ? 1 : 0)}
            />
          </div>

          <p>
            Ces cookies peuvent être mis en place au sein de notre site Web par
            nos partenaires publicitaires. Ils peuvent être utilisés par ces
            sociétés pour établir un profil de vos intérêts et vous proposer des
            publicités pertinentes sur d'autres sites Web. Ils ne stockent pas
            directement des données personnelles, mais sont basés sur
            l'identification unique de votre navigateur et de votre appareil
            Internet. Si vous n'autorisez pas ces cookies, votre publicité sera
            moins ciblée.
          </p>
          <ul>
            <li>Google Ads transaction : publicité</li>
            <li>Remarketing Google Ads : publicité</li>
          </ul>
        </div>

        <Flex
          style={{ justifyContent: 'flex-end' }}
          additionalClassName="footer"
        >
          <button
            type="button"
            className="InputSubmitBase-button"
            onClick={submit}
          >
            Sauvegarder les paramètres
          </button>
        </Flex>
      </div>
    </div>
  );
}

export default (withInstance(
  withRouter(CookieSettingsPage)
): React.ComponentType<OwnProps>);
